import React from 'react';
import { useParams, useLocation, Navigate, Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Clock, BookOpen } from 'lucide-react';
import SiteLayout, { PALETTE } from './SiteLayout';
import SEO from '../../components/SEO';
import { NewsletterSignup } from '../../components/NewsletterSignup';
import { GUIDES_BY_SLUG } from '../../seo/guidesConfig';
import { splitLangPath, getDict, localizeDeep, langHref, tr, MARKETING_LANGS } from '../../i18n/marketing';
import { ORG_JSONLD, buildFAQ, buildBreadcrumb, SITE_URL } from '../../seo/landingConfig';

const serif = "'Playfair Display', Georgia, serif";

const SectionBlock = ({ section }) => (
  <section id={section.id} style={{ marginTop: 52, scrollMarginTop: 90 }}>
    <h2 style={{ fontFamily: serif, fontSize: 'clamp(22px, 2.6vw, 30px)', fontWeight: 700, color: PALETTE.ink, letterSpacing: '-0.015em', lineHeight: 1.15, margin: 0 }}>
      {section.h2}
    </h2>
    {(section.paragraphs || []).map((p, i) => (
      <p key={i} style={{ fontSize: 16, lineHeight: 1.75, color: PALETTE.slate, marginTop: 18 }}>{p}</p>
    ))}
    {section.steps && (
      <ol style={{ listStyle: 'none', padding: 0, margin: '26px 0 0', counterReset: 'step' }}>
        {section.steps.map((s, i) => (
          <li key={s.t} style={{ display: 'flex', gap: 16, padding: '18px 0', borderTop: i > 0 ? `1px solid ${PALETTE.border}` : 'none' }}>
            <div style={{ flexShrink: 0, width: 34, height: 34, borderRadius: '50%', background: PALETTE.ink, color: PALETTE.gold, fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</div>
            <div>
              <div style={{ fontSize: 15.5, fontWeight: 700, color: PALETTE.ink }}>{s.t}</div>
              <div style={{ fontSize: 14.5, color: PALETTE.slate, lineHeight: 1.65, marginTop: 4 }}>{s.d}</div>
            </div>
          </li>
        ))}
      </ol>
    )}
    {section.bullets && (
      <div style={{ marginTop: 26, display: 'grid', gap: 14 }}>
        {section.bullets.map((b) => (
          <div key={b.t} style={{ background: '#fff', border: `1px solid ${PALETTE.border}`, borderLeft: `3px solid ${PALETTE.gold}`, padding: '16px 20px' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: PALETTE.ink }}>{b.t}</div>
            <div style={{ fontSize: 14, color: PALETTE.slate, lineHeight: 1.6, marginTop: 4 }}>{b.d}</div>
          </div>
        ))}
      </div>
    )}
    {section.table && (
      <div style={{ marginTop: 26, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', border: `1px solid ${PALETTE.border}`, minWidth: 520 }}>
          <thead>
            <tr style={{ background: PALETTE.ink, color: '#fff' }}>
              {section.table.head.map((h) => (
                <th key={h} style={{ padding: '11px 14px', textAlign: 'left', fontSize: 12, letterSpacing: '0.05em', fontWeight: 700 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {section.table.rows.map((row, ri) => (
              <tr key={ri} style={{ background: ri % 2 ? PALETTE.cream : '#fff', borderTop: `1px solid ${PALETTE.border}`, verticalAlign: 'top' }}>
                {row.map((cell, ci) => (
                  <td key={ci} style={{ padding: '11px 14px', fontSize: 13.5, color: ci === 0 ? PALETTE.ink : PALETTE.slate, fontWeight: ci === 0 ? 600 : 400, lineHeight: 1.55 }}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
    {(section.paragraphs2 || []).map((p, i) => (
      <p key={i} style={{ fontSize: 16, lineHeight: 1.75, color: PALETTE.slate, marginTop: 18 }}>{p}</p>
    ))}
    {section.callout && (
      <div style={{ marginTop: 26, background: PALETTE.ink, color: '#fff', padding: '22px 26px', borderRadius: 6, borderLeft: `4px solid ${PALETTE.gold}` }}>
        <div style={{ fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.gold }}>{section.callout.title}</div>
        <div style={{ fontSize: 14.5, lineHeight: 1.7, marginTop: 8, color: '#E2E8F0' }}>{section.callout.body}</div>
      </div>
    )}
  </section>
);

export default function GuideArticle() {
  const { slug } = useParams();
  const { pathname } = useLocation();
  const { lang } = splitLangPath(pathname);
  const dict = lang !== 'en' ? getDict(lang) : null;
  const baseGuide = GUIDES_BY_SLUG[slug];
  const guide = baseGuide && dict ? localizeDeep(baseGuide, dict) : baseGuide;
  if (!guide) return <Navigate to={langHref(lang, '/guides')} replace />;

  const t = (s) => tr(dict, s);
  const gp = langHref(lang, `/guides/${guide.slug}`);
  const url = `${SITE_URL}${gp}`;
  const alternates = [
    { hreflang: 'en', href: `${SITE_URL}/guides/${guide.slug}` },
    ...MARKETING_LANGS.map((l) => ({ hreflang: l, href: `${SITE_URL}/${l}/guides/${guide.slug}` })),
    { hreflang: 'x-default', href: `${SITE_URL}/guides/${guide.slug}` },
  ];
  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Guides', url: '/guides' },
    { name: guide.title, url: `/guides/${guide.slug}` },
  ];
  const jsonLd = [
    ORG_JSONLD,
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: guide.title,
      description: guide.metaDescription,
      datePublished: guide.datePublished,
      dateModified: guide.dateModified,
      url,
      mainEntityOfPage: { '@type': 'WebPage', '@id': url },
      author: { '@type': 'Organization', name: 'CloudVoro', '@id': `${SITE_URL}/#organization` },
      publisher: { '@type': 'Organization', name: 'CloudVoro', '@id': `${SITE_URL}/#organization`, logo: { '@type': 'ImageObject', url: `${SITE_URL}/brochure-assets/logos/keystone-horizontal-light-2048.png` } },
    },
    buildBreadcrumb(breadcrumbs),
    guide.faqs && guide.faqs.length ? buildFAQ(guide.faqs) : null,
  ].filter(Boolean);

  const related = (guide.relatedGuides || []).map((s) => GUIDES_BY_SLUG[s]).filter(Boolean).map((r) => (dict ? { ...r, title: tr(dict, r.title) } : r));

  return (
    <SiteLayout>
      <SEO title={guide.metaTitle} description={guide.metaDescription} path={gp} keywords={guide.keywords} type="article" lang={lang} alternates={alternates} jsonLd={jsonLd} />

      {/* HERO */}
      <section data-testid={`guide-hero-${guide.slug}`} style={{ background: PALETTE.cream, padding: '120px 32px 64px', borderBottom: `1px solid ${PALETTE.border}` }}>
        <div style={{ maxWidth: 860, margin: '0 auto' }}>
          <nav aria-label="Breadcrumb" style={{ fontSize: 12, color: PALETTE.muted, marginBottom: 22 }}>
            <Link to="/" style={{ color: PALETTE.muted, textDecoration: 'none' }}>{t('Home')}</Link>
            <span style={{ margin: '0 8px', color: PALETTE.border }}>/</span>
            <Link to={langHref(lang, '/guides')} style={{ color: PALETTE.muted, textDecoration: 'none' }}>{t('Guides')}</Link>
            <span style={{ margin: '0 8px', color: PALETTE.border }}>/</span>
            <span style={{ color: PALETTE.ink, fontWeight: 600 }}>{guide.title}</span>
          </nav>
          <div style={{ fontSize: 11, letterSpacing: '0.24em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginBottom: 16 }}>{guide.eyebrow}</div>
          <h1 style={{ fontFamily: serif, fontSize: 'clamp(30px, 4vw, 50px)', fontWeight: 700, color: PALETTE.ink, margin: 0, letterSpacing: '-0.02em', lineHeight: 1.08 }}>
            {guide.h1}<br />
            <em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.magenta }}>{guide.h1Italic}</em>
          </h1>
          <p style={{ fontSize: 17, lineHeight: 1.7, color: PALETTE.slate, marginTop: 22 }}>{guide.lead}</p>
          <div style={{ display: 'flex', gap: 20, marginTop: 22, fontSize: 13, color: PALETTE.muted, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Clock size={14} /> {guide.readMinutes} {t('min read')}</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><BookOpen size={14} /> Updated {guide.dateModified}</span>
            <span>{t('By the CloudVoro team')}</span>
          </div>
        </div>
      </section>

      {/* BODY */}
      <section style={{ background: '#fff', padding: '56px 32px 80px' }}>
        <div style={{ maxWidth: 860, margin: '0 auto' }}>
          {/* TOC */}
          <div data-testid="guide-toc" style={{ background: PALETTE.paper, border: `1px solid ${PALETTE.border}`, borderRadius: 6, padding: '20px 24px' }}>
            <div style={{ fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.muted, marginBottom: 10 }}>{t('In this guide')}</div>
            {guide.sections.map((s, i) => (
              <a key={s.id} href={`#${s.id}`} style={{ display: 'block', fontSize: 14, color: PALETTE.ink, textDecoration: 'none', padding: '5px 0', fontWeight: 500 }}>
                <span style={{ color: PALETTE.magenta, fontWeight: 700, marginRight: 10 }}>{String(i + 1).padStart(2, '0')}</span>{s.h2}
              </a>
            ))}
          </div>

          {guide.sections.map((s) => <SectionBlock key={s.id} section={s} />)}

          {/* KEY TAKEAWAYS */}
          {guide.keyTakeaways && (
            <div data-testid="guide-takeaways" style={{ marginTop: 56, background: PALETTE.cream, border: `1px solid ${PALETTE.border}`, borderTop: `3px solid ${PALETTE.magenta}`, padding: '26px 30px' }}>
              <div style={{ fontFamily: serif, fontSize: 22, fontWeight: 700, color: PALETTE.ink }}>{t('Key takeaways')}</div>
              <ul style={{ listStyle: 'none', padding: 0, margin: '16px 0 0' }}>
                {guide.keyTakeaways.map((t) => (
                  <li key={t} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '7px 0', fontSize: 14.5, color: PALETTE.ink, lineHeight: 1.55 }}>
                    <CheckCircle2 size={17} color={PALETTE.magenta} style={{ flexShrink: 0, marginTop: 3 }} />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* NEWSLETTER */}
          <div style={{ marginTop: 40 }}>
            <NewsletterSignup source={`guide:${guide.slug}`} />
          </div>

          {/* FAQ */}
          {guide.faqs && guide.faqs.length > 0 && (
            <div style={{ marginTop: 56 }}>
              <h2 style={{ fontFamily: serif, fontSize: 'clamp(22px, 2.6vw, 30px)', fontWeight: 700, color: PALETTE.ink, margin: '0 0 24px' }}>{t('Frequently asked questions')}</h2>
              <div style={{ display: 'grid', gap: 14 }}>
                {guide.faqs.map((f) => (
                  <details key={f.q} style={{ background: PALETTE.paper, border: `1px solid ${PALETTE.border}`, borderRadius: 4, padding: '15px 20px' }}>
                    <summary style={{ cursor: 'pointer', fontSize: 15, fontWeight: 700, color: PALETTE.ink, listStyle: 'none' }}>{f.q}</summary>
                    <div style={{ marginTop: 11, fontSize: 14, color: PALETTE.slate, lineHeight: 1.65 }}>{f.a}</div>
                  </details>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA + RELATED */}
      <section style={{ background: PALETTE.ink, color: '#fff', padding: '72px 32px' }}>
        <div style={{ maxWidth: 1080, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 48 }} className="cta-grid">
          <div>
            <h2 style={{ fontFamily: serif, fontSize: 'clamp(24px, 3vw, 34px)', fontWeight: 700, margin: 0, color: '#fff', lineHeight: 1.12 }}>
              {t('See this done in software, on real production data.')}
            </h2>
            <p style={{ fontSize: 15, color: '#CBD5E1', marginTop: 16, lineHeight: 1.65 }}>
              {t('Keystone runs the full chain, supplier delivery → batch → dispatch → customer, in under 30 seconds. 20-minute discovery call, no sales pitch.')}
            </p>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 22 }}>
              <Link to="/site/contact" data-testid={`guide-cta-${guide.slug}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '13px 24px', background: PALETTE.magenta, color: '#fff', fontSize: 14, fontWeight: 700, textDecoration: 'none', borderRadius: 4 }}>
                {t('Book a discovery call')} <ArrowRight size={16} />
              </Link>
              {(guide.productLinks || []).slice(0, 1).map((p) => (
                <Link key={p.to} to={p.to} style={{ display: 'inline-flex', alignItems: 'center', padding: '13px 24px', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', fontSize: 14, fontWeight: 600, textDecoration: 'none', borderRadius: 4 }}>
                  {p.label}
                </Link>
              ))}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.gold, marginBottom: 16 }}>{t('Keep reading')}</div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {related.map((r) => (
                <li key={r.slug} style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                  <Link to={langHref(lang, `/guides/${r.slug}`)} data-testid={`related-guide-${r.slug}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 0', color: '#fff', textDecoration: 'none', fontSize: 14.5 }}>
                    {r.title}
                    <ArrowRight size={16} color={PALETTE.gold} style={{ flexShrink: 0 }} />
                  </Link>
                </li>
              ))}
              {(guide.productLinks || []).slice(1).map((p) => (
                <li key={p.to} style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                  <Link to={p.to} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 0', color: '#CBD5E1', textDecoration: 'none', fontSize: 14 }}>
                    {p.label}
                    <ArrowRight size={16} color={PALETTE.gold} style={{ flexShrink: 0 }} />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <style>{`
          @media (max-width: 760px) {
            .cta-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
          }
        `}</style>
      </section>
    </SiteLayout>
  );
}
